import type { AxiosInstance } from 'axios';

export type CountryStatus = 'ACTIVE' | 'COMING_SOON' | 'DISABLED';
export type TaxRuleKind = 'DEFAULT' | 'CATEGORY' | 'PRODUCT';

export interface Country {
  id: string;
  code: string;
  name: string;
  currencyCode: string;
  vatRateBps: number;
  region: string | null;
  defaultLocale: string;
  status: CountryStatus;
  createdAt: string;
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  minorUnits: number;
  rateToInrX10000: number;
  rateUpdatedAt: string | null;
  active: boolean;
}

export interface Locale {
  id: string;
  code: string;
  name: string;
  nativeName: string | null;
  rtl: boolean;
  active: boolean;
}

export interface TaxRule {
  id: string;
  countryCode: string;
  kind: TaxRuleKind;
  hsnSac: string | null;
  variantId: string | null;
  rateBps: number;
  inclusive: boolean;
  label: string | null;
  active: boolean;
  validFrom: string;
  validTo: string | null;
}

export interface ConversionResult {
  amountMinor: number;
  fromCurrency: string;
  toCurrency: string;
  rateX10000: number;
  convertedMinor: number;
}

export interface TaxQuoteResult {
  ruleId: string | null;
  rateBps: number;
  netMinor: number;
  taxMinor: number;
  grossMinor: number;
  label: string | null;
}

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function globalizationEndpoints(http: AxiosInstance) {
  return {
    listCountries: () => unwrap<Country[]>(http.get('/globalization/countries')),
    listCurrencies: () => unwrap<Currency[]>(http.get('/globalization/currencies')),
    listLocales: () => unwrap<Locale[]>(http.get('/globalization/locales')),
    convert: (body: { amountMinor: number; fromCurrency: string; toCurrency: string }) =>
      http.post<ConversionResult>('/globalization/convert', body).then((r) => r.data),
    quoteTax: (body: { countryCode: string; amountMinor: number; hsnSac?: string; inclusive?: boolean }) =>
      http.post<TaxQuoteResult>('/globalization/tax/quote', body).then((r) => r.data),

    // Admin
    upsertCountry: (body: {
      code: string;
      name: string;
      currencyCode: string;
      vatRateBps: number;
      region?: string;
      defaultLocale?: string;
      status?: CountryStatus;
    }) => http.put<Country>('/admin/globalization/countries', body).then((r) => r.data),
    upsertCurrency: (body: {
      code: string;
      name: string;
      symbol: string;
      minorUnits?: number;
      rateToInrX10000?: number;
      active?: boolean;
    }) => http.put<Currency>('/admin/globalization/currencies', body).then((r) => r.data),
    recordFxRate: (body: { fromCurrency: string; toCurrency: string; rateX10000: number; source?: string }) =>
      http.post('/admin/globalization/fx/record', body).then((r) => r.data),
    upsertLocale: (body: { code: string; name: string; nativeName?: string; rtl?: boolean; active?: boolean }) =>
      http.put<Locale>('/admin/globalization/locales', body).then((r) => r.data),
    upsertTranslation: (body: { locale: string; namespace?: string; key: string; value: string }) =>
      http.put('/admin/globalization/translations', body).then((r) => r.data),
    listTaxRules: () => unwrap<TaxRule[]>(http.get('/admin/globalization/tax/rules')),
    upsertTaxRule: (body: {
      countryCode: string;
      kind: TaxRuleKind;
      hsnSac?: string;
      rateBps: number;
      inclusive?: boolean;
      label?: string;
      active?: boolean;
    }) => http.post<TaxRule>('/admin/globalization/tax/rules', body).then((r) => r.data),
  };
}

export type GlobalizationEndpoints = ReturnType<typeof globalizationEndpoints>;
